import moment from "moment";
import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { bindActionCreators, Dispatch } from "redux";
import styled from "styled-components";
import {
  Booking,
  Chat,
  displayLocation,
  Event,
  generateDurationOptions,
  generateTimeOptions,
  getOptionTitle,
  getVenuePrimaryPhotoUrl,
  Inquiry
} from "sweet-global";
import GuestsIcon from "sweet-web/components/icons/GuestsIcon";
import OutlineButton from "sweet-web/components/universal/Button/OutlineButton";
import Input from "sweet-web/components/universal/Input";
import DateInput from "sweet-web/components/universal/Input/DateInput";
import OptionInput from "sweet-web/components/universal/Input/OptionInput";
import PriceInput from "sweet-web/components/universal/Input/PriceInput";
import SelectInput from "sweet-web/components/universal/Input/SelectInput";
import BookPricing from "sweet-web/components/Venue/BookPricing";
import { fadeIn } from "sweet-web/lib/animations";
import { colors } from "sweet-web/lib/styles";
import { RootState } from "sweet-web/redux";
import {
  editBooking,
  loadEditableBooking
} from "sweet-web/redux/booking/actions";
import * as selectors from "sweet-web/redux/selectors";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
`;

const InputRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const BookedVenueWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 15px 0;
`;

const BookedVenueText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const BookedVenueTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.nearBlack};
  margin-bottom: 3px;
`;

const BookedVenueAddress = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.gray};
`;

const BookedVenueImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 5px;
  object-fit: cover;
`;

type OwnProps = {
  bookingId: string;
};

type Props = OwnProps & ConnectedProps<typeof connector>;

const BookingComponent: React.FC<Props> = props => {
  const { bookingId, venue, userType, booking, bookingEntity } = props;

  const [localBooking, setLocalBooking] = useState(
    bookingEntity as Inquiry & Event
  );

  useEffect(() => {
    setLocalBooking(booking);
  }, [booking]);

  useEffect(() => {
    if (bookingEntity)
      props.loadEditableBooking(bookingEntity as Inquiry & Event);
  }, [bookingId]);

  useEffect(() => {
    if (bookingEntity)
      props.loadEditableBooking(bookingEntity as Inquiry & Event);
  }, [bookingEntity]);

  const timeOptions = generateTimeOptions();
  const durationOptions = generateDurationOptions();

  if (!props.bookingEntity) return <BookPricing venueId={booking.venue} />;
  if (!localBooking) return null;
  if (!props.booking) return null;

  const venuePrimaryPhotoUrl = getVenuePrimaryPhotoUrl(venue);

  const save = () => {
    props.editBooking(localBooking as Inquiry & Event);
  };

  return (
    <Wrapper>
      {userType === "USER" && (
        <BookedVenueWrapper>
          <BookedVenueText>
            <BookedVenueTitle>{venue.name}</BookedVenueTitle>
            <BookedVenueAddress>
              {displayLocation(venue.location)}
            </BookedVenueAddress>
          </BookedVenueText>
          {venuePrimaryPhotoUrl && (
            <BookedVenueImage src={venuePrimaryPhotoUrl} />
          )}
        </BookedVenueWrapper>
      )}
      <InputRow>
        <DateInput
          title="Date"
          date={localBooking.date}
          onDateChange={newDate =>
            props.editBooking({
              date: newDate
            })
          }
          style={{ flex: 1, marginRight: 5 }}
        />
        <Input
          style={{ flex: 1, marginLeft: 5 }}
          icon={<GuestsIcon size={14} color={colors.gray} />}
          title={"Guests"}
          type="number"
          value={localBooking.guests ? String(localBooking.guests) : ""}
          onChange={value =>
            setLocalBooking(b => ({
              ...b,
              guests: Number(value)
            }))
          }
        />
      </InputRow>
      <InputRow>
        <SelectInput
          style={{ flex: 1, marginRight: 5 }}
          title="Start Time"
          value={localBooking.startTime}
          options={timeOptions}
          onChange={value => {
            props.editBooking({ startTime: value });
          }}
        />
        <SelectInput
          style={{ flex: 1, marginLeft: 5 }}
          title="Duration"
          value={localBooking.length ? String(localBooking.length) : ""}
          options={durationOptions}
          onChange={value => {
            props.editBooking({ length: Number(value) });
          }}
        />
      </InputRow>
      {Object.keys(venue.spaces).length > 0 && (
        <OptionInput
          title="Space"
          value={localBooking.spaces}
          onChange={value => props.editBooking({ spaces: value })}
          options={Object.keys(venue.spaces).map(spaceId => {
            const space = venue.spaces[spaceId];

            return {
              id: spaceId,
              title: space.name,
              description: `Capacity: ${space.capacity}`
            };
          })}
        />
      )}
      {Object.keys(venue.options).length > 0 && (
        <OptionInput
          title="Options"
          value={localBooking.options}
          onChange={value => props.editBooking({ options: value })}
          options={Object.keys(venue.options).map(optionId => {
            const option = venue.options[optionId];

            return {
              id: optionId,
              title: getOptionTitle(option),
              cost: option.startingCost
            };
          })}
        />
      )}
      <Input
        title={"Comments"}
        value={localBooking.comments}
        multiline={true}
        onChange={value => {
          setLocalBooking(b => ({
            ...b,
            comments: value
          }));
        }}
        onBlur={save}
      />
      {!(booking.status === "INQUIRY" && userType === "USER") &&
        !(
          booking.status === "CANCELLED" &&
          (!booking.deposit || (booking.deposit && !booking.deposit.amount))
        ) && (
          <InputRow>
            <PriceInput
              style={{ flex: 1, marginRight: 5 }}
              title={"Deposit"}
              value={localBooking.deposit}
              onChange={deposit => {
                setLocalBooking(b => ({
                  ...b,
                  deposit
                }));
              }}
              onBlur={save}
            />
            <PriceInput
              style={{ flex: 1, marginLeft: 5 }}
              title={"Total Price"}
              value={localBooking.rental}
              onChange={rental => {
                setLocalBooking(b => ({
                  ...b,
                  rental
                }));
              }}
              onBlur={save}
            />
          </InputRow>
        )}
    </Wrapper>
  );
};

const mapState = (state: RootState, ownProps: OwnProps) => {
  return {
    bookingEntity: selectors.getBooking(ownProps.bookingId)(state),
    booking: selectors.editableBooking(state),
    venue: selectors.getVenueByBooking(ownProps.bookingId)(state)(state),
    userType: selectors.userType(state)
  };
};
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      loadEditableBooking,
      editBooking
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default connector(BookingComponent);
